// Keep the complete persisted auth snapshot together: it includes both the
// active session and (during impersonation) the administrator's return session.
// A null secure backend explicitly selects AsyncStorage for old mobile binaries.
// Secure-capable binaries migrate the snapshot and never downgrade on errors.
export function createSecureAuthStorage(ordinary, secure, onError = () => {
  console.warn('Could not persist the session. Unlock the device and sign in again.');
}) {
  const key = 'auth-storage';
  const signedOutKey = 'auth-storage.secure-signed-out-v1';
  const credentials = secure === null ? ordinary : secure;
  let queue = Promise.resolve(), mutation = 0;
  let verifiedSnapshot = null;
  const serial = operation => {
    const result = queue.then(operation);
    queue = result.catch(() => {});
    return result;
  };
  const parse = value => {
    const payload = JSON.parse(value);
    if (!payload || typeof payload.state !== 'object' || !payload.state || Array.isArray(payload.state)) {
      throw new Error('Invalid saved session.');
    }
    return payload.state;
  };
  const clear = async () => {
    verifiedSnapshot = null;
    // Durable fence first: even if Keychain deletion fails, a later launch may
    // never restore this session. This marker contains no credentials.
    await ordinary.setItem(signedOutKey, '1');
    await ordinary.removeItem(key);
    if (secure !== null) await secure.removeItem(key);
  };
  return {
    getItem(name) {
      if (name !== key) return ordinary.getItem(name);
      const generation = mutation;
      return serial(async () => {
        verifiedSnapshot = null;
        if (await ordinary.getItem(signedOutKey) === '1') {
          await clear();
          return null;
        }
        const saved = secure === null ? null : await secure.getItem(key);
        if (saved !== null) {
          const state = parse(saved);
          if (!state.token && !state.refreshToken) { await clear(); return null; }
          await ordinary.removeItem(key);
          verifiedSnapshot = saved;
          return saved;
        }
        const legacy = await ordinary.getItem(key);
        if (legacy !== null) {
          const state = parse(legacy);
          // Older logout paths could leave a stashed administrator session
          // behind. An otherwise signed-out snapshot must never restore it.
          if (!state.token && !state.refreshToken) { await clear(); return null; }
          if (secure === null) { verifiedSnapshot = legacy; return legacy; }
          await secure.setItem(key, legacy);
          if (await secure.getItem(key) !== legacy) throw new Error('Could not verify saved session.');
          // Never delete the old copy before the exact secure copy is verified.
          await ordinary.removeItem(key);
          verifiedSnapshot = legacy;
        }
        return legacy;
      }).then(value => {
        // A login/logout can happen while the secure store is responding. Do
        // not let Zustand merge that older snapshot over the current session.
        if (mutation !== generation) throw new Error('Session changed while restoring it.');
        return value;
      });
    },
    setItem(name, value) {
      if (name !== key) return ordinary.setItem(name, value);
      let state;
      try { state = parse(value); } catch { onError(); return Promise.resolve(); }
      // Zustand can persist empty initial/loading state while hydration is in
      // flight or after a failed read. Only explicit removeItem (all native
      // logout/token-clear paths) has authority to delete a saved session.
      if (!state.token && !state.refreshToken) return Promise.resolve();
      mutation++;
      return serial(async () => {
        // Zustand persists after transient updates too. Work streaming and
        // balance polling must not rewrite/read back an identical Keychain
        // snapshot each time. Compare inside the queue so rotation, logout,
        // migration failures and overlapping writes retain their ordering.
        if (value === verifiedSnapshot) return;
        // A write can reach the device before readback/cleanup fails. Do not
        // retain a cache of the previous value across that uncertain state.
        verifiedSnapshot = null;
        await credentials.setItem(key, value);
        if (await credentials.getItem(key) !== value) throw new Error('Could not verify saved session.');
        if (secure !== null) await ordinary.removeItem(key);
        await ordinary.removeItem(signedOutKey);
        verifiedSnapshot = value;
      }).catch(() => { onError(); });
    },
    removeItem(name) {
      if (name !== key) return ordinary.removeItem(name);
      mutation++;
      return serial(clear).catch(() => { onError(); });
    },
  };
}
